import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Truck, MapPin, Clock } from 'lucide-react';
import { useTasks } from '../../context/TasksContext';
import { useLeads } from '../../context/LeadsContext';
import { useRole } from '../../context/RoleContext';
import { useAuth } from '../../context/AuthContext';
import WorkOrderSheet from '../../features/clients/components/profile/WorkOrderSheet';

// ── Status config ──────────────────────────────────────────────────────────────
const STATUS_CFG = {
  approved:  { label: 'Ready',    dot: 'bg-gray-300',    text: 'text-gray-400'    },
  scheduled: { label: 'Ready',    dot: 'bg-gray-300',    text: 'text-gray-400'    },
  en_route:  { label: 'En Route', dot: 'bg-amber-500',   text: 'text-amber-700'   },
  on_site:   { label: 'On Site',  dot: 'bg-indigo-500',  text: 'text-indigo-700'  },
  completed: { label: 'Done',     dot: 'bg-emerald-500', text: 'text-emerald-600' },
};

// ── Helpers ────────────────────────────────────────────────────────────────────
function isSameDay(dateStr, ref) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return false;
  return (
    d.getFullYear() === ref.getFullYear() &&
    d.getMonth()    === ref.getMonth() &&
    d.getDate()     === ref.getDate()
  );
}

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const h    = d.getHours() % 12 || 12;
  const m    = d.getMinutes();
  const ampm = d.getHours() < 12 ? 'AM' : 'PM';
  return m === 0 ? `${h} ${ampm}` : `${h}:${String(m).padStart(2, '0')} ${ampm}`;
}

function formatDateLabel(date) {
  const today = new Date();
  const isToday =
    date.getDate()     === today.getDate() &&
    date.getMonth()    === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
  const label = date.toLocaleDateString('en-US', {
    weekday: 'long',
    month:   'long',
    day:     'numeric',
  });
  return isToday ? `Today — ${label}` : label;
}

function getAddress(lead) {
  if (!lead) return null;
  if (lead.addressDisplay) return lead.addressDisplay;
  const c = lead.contact || {};
  const parts = [c.addressLine1 || lead.addressLine1, c.city || lead.city, c.state || lead.state];
  const joined = parts.filter(Boolean).join(', ');
  if (joined) return joined;
  const a = lead.address;
  if (!a) return null;
  if (typeof a === 'string') return a;
  if (typeof a === 'object') return [a.line1, a.city, a.state].filter(Boolean).join(', ');
  return null;
}

// ── TechCard ───────────────────────────────────────────────────────────────────
function TechCard({ name, jobs, leadsMap, onOpen, unassigned = false }) {
  return (
    <div
      className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      data-testid={`dispatch-mobile-tech-${unassigned ? 'unassigned' : name.replace(/\s+/g, '-').toLowerCase()}`}
    >
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 border-b border-gray-100 ${unassigned ? 'bg-gray-50' : ''}`}>
        <span className={`font-semibold text-sm ${unassigned ? 'text-gray-400' : 'text-gray-900'}`}>
          {name}
        </span>
        <span className="text-xs text-gray-400 font-medium">
          {jobs.length} job{jobs.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Job rows */}
      <div className="divide-y divide-gray-50">
        {jobs.map((job) => {
          const lead = leadsMap[job.leadId];
          const cfg  = STATUS_CFG[job.status] || STATUS_CFG.scheduled;
          const addr = getAddress(lead);
          const time = fmtTime(job.scheduledStart);
          const done = job.status === 'completed';

          return (
            <button
              key={job.id}
              type="button"
              onClick={() => onOpen(job)}
              className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 active:bg-gray-100 transition-colors text-left"
              data-testid={`dispatch-mobile-job-${job.id}`}
            >
              {/* Status dot */}
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${cfg.dot}`} />

              {/* Client + address */}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${done ? 'text-gray-300 line-through' : 'text-gray-900'}`}>
                  {job.clientName || lead?.name || 'Unknown'}
                </p>
                {addr && !done && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <MapPin className="h-3 w-3 text-gray-300 shrink-0" />
                    <span className="text-xs text-gray-400 truncate">{addr}</span>
                  </div>
                )}
              </div>

              {/* Time + status label */}
              <div className="flex flex-col items-end gap-0.5 shrink-0">
                {time && (
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3 text-gray-300" />
                    <span className="text-xs text-gray-500">{time}</span>
                  </div>
                )}
                <span className={`text-[10px] font-semibold ${cfg.text}`}>{cfg.label}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
const DispatchMobileSummary = () => {
  const { tasks }        = useTasks();
  const { leads }        = useLeads();
  const { isTechnician } = useRole();
  const { user }         = useAuth();

  const [dispatchDate, setDispatchDate] = useState(() => new Date());
  const [selectedTask, setSelectedTask] = useState(null);
  const [sheetOpen, setSheetOpen]       = useState(false);

  const leadsMap = useMemo(() => {
    const m = {};
    leads.forEach((l) => { m[l.id] = l; });
    return m;
  }, [leads]);

  // All jobs for the selected date
  const dateJobs = useMemo(() =>
    tasks.filter((t) => t.type === 'job' && isSameDay(t.scheduledStart, dispatchDate)),
    [tasks, dispatchDate]
  );

  // Technicians only see their own jobs; admins/owners see all
  const visibleJobs = useMemo(() => {
    if (isTechnician && user?.uid) {
      return dateJobs.filter((t) => t.assignedTo === user.uid || !t.assignedTo);
    }
    return dateJobs;
  }, [dateJobs, isTechnician, user?.uid]);

  // Build tech name map
  const techMap = useMemo(() => {
    const m = {};
    visibleJobs.forEach((t) => {
      if (t.assignedTo && !m[t.assignedTo]) {
        m[t.assignedTo] = t.assignedToName || t.assignedTo;
      }
    });
    return m;
  }, [visibleJobs]);

  // Grouped by tech, sorted by dispatchOrder / scheduledStart
  const techGroups = useMemo(() =>
    Object.keys(techMap).map((uid) => ({
      uid,
      name: techMap[uid],
      jobs: visibleJobs
        .filter((t) => t.assignedTo === uid)
        .sort((a, b) => {
          if (a.dispatchOrder != null && b.dispatchOrder != null) return a.dispatchOrder - b.dispatchOrder;
          return (a.scheduledStart || '').localeCompare(b.scheduledStart || '');
        }),
    })),
    [techMap, visibleJobs]
  );

  const unassignedJobs = useMemo(() =>
    visibleJobs
      .filter((t) => !t.assignedTo)
      .sort((a, b) => (a.scheduledStart || '').localeCompare(b.scheduledStart || '')),
    [visibleJobs]
  );

  // Stats strip
  const stats = useMemo(() => {
    const total     = visibleJobs.length;
    const completed = visibleJobs.filter((t) => t.status === 'completed').length;
    const inFlight  = visibleJobs.filter((t) => t.status === 'en_route' || t.status === 'on_site').length;
    return { total, completed, inFlight };
  }, [visibleJobs]);

  const shiftDate = (delta) => {
    setDispatchDate((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + delta));
  };

  const openJob = (task) => {
    setSelectedTask(task);
    setSheetOpen(true);
  };

  const closeSheet = () => {
    setSheetOpen(false);
    setSelectedTask(null);
  };

  const selectedLead = selectedTask ? leadsMap[selectedTask.leadId] : null;

  return (
    <div className="min-h-screen bg-gray-50 pb-20" data-testid="dispatch-mobile-summary">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div
        className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10"
        style={{ paddingTop: 'max(16px, env(safe-area-inset-top, 16px))' }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-[var(--cb-orange)]" />
            <h1 className="text-lg font-bold text-gray-900">Today's Operations</h1>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => shiftDate(-1)}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 active:bg-gray-200 transition-colors"
              data-testid="dispatch-mobile-prev"
              aria-label="Previous day"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => shiftDate(1)}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 active:bg-gray-200 transition-colors"
              data-testid="dispatch-mobile-next"
              aria-label="Next day"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-0.5">{formatDateLabel(dispatchDate)}</p>
      </div>

      {/* ── Stats strip ─────────────────────────────────────────────────────── */}
      {stats.total > 0 && (
        <div className="bg-white border-b border-gray-100 px-4 py-2.5 flex items-center gap-4">
          <span className="text-sm font-semibold text-gray-700">{stats.total} job{stats.total !== 1 ? 's' : ''}</span>
          {stats.inFlight > 0 && (
            <span className="text-sm text-amber-600 font-medium">{stats.inFlight} in flight</span>
          )}
          {stats.completed > 0 && (
            <span className="text-sm text-emerald-600 font-medium">{stats.completed} complete</span>
          )}
        </div>
      )}

      {/* ── Tech cards + unassigned ──────────────────────────────────────────── */}
      <div className="px-4 py-4 space-y-4">
        {techGroups.map(({ uid, name, jobs }) => (
          <TechCard key={uid} name={name} jobs={jobs} leadsMap={leadsMap} onOpen={openJob} />
        ))}

        {unassignedJobs.length > 0 && (
          <TechCard
            name="Unassigned"
            jobs={unassignedJobs}
            leadsMap={leadsMap}
            onOpen={openJob}
            unassigned
          />
        )}

        {/* Empty state */}
        {visibleJobs.length === 0 && (
          <div className="text-center py-20" data-testid="dispatch-mobile-empty">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Truck className="h-8 w-8 text-gray-300" />
            </div>
            <p className="text-gray-700 font-semibold">Nothing scheduled</p>
            <p className="text-gray-400 text-sm mt-1">Use desktop to plan and assign jobs</p>
          </div>
        )}
      </div>

      {/* ── WorkOrderSheet ───────────────────────────────────────────────────── */}
      <WorkOrderSheet
        task={selectedTask}
        client={selectedLead}
        open={sheetOpen}
        onClose={closeSheet}
        onCompleted={closeSheet}
      />
    </div>
  );
};

export default DispatchMobileSummary;
